import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import { Play, Pause, Square } from "lucide-react";
import { LIFE_JOURNEY } from "@/data/lifeJourney";
import { seaRoute } from "@/data/seaRoutes";

const CH_COLORS = { I: "#d4af37", II: "#c97f4a", III: "#5ec4b6" };
const CH_LABEL = {
  I: "Chapter I · The Navy",
  II: "Chapter II · Career",
  III: "Chapter III · The World",
};

function distKm(a, b) {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((a.lat * Math.PI) / 180) *
      Math.cos((b.lat * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}
const zoomForLeg = (d) => (d < 400 ? 5.5 : d < 2500 ? 4.5 : 3.5);

// Ship legs (consecutive ports of the same navy draft or cruise) sail the
// sea lanes; everything else is assumed to be a flight (straight, dashed).
const isSeaLeg = (a, b) =>
  a.chapter === b.chapter &&
  a.title === b.title &&
  (b.chapter === "I" || b.chapter === "III");

// "Play His Life" — replays Dad's whole journey, 1962 to today, as a
// moving marker with a growing trail and a live caption card.
export default function VoyageCinema({ mapRef, onActiveChange }) {
  const [status, setStatus] = useState("idle"); // idle | playing | paused | done
  const [speed, setSpeed] = useState(1);
  const [caption, setCaption] = useState(null);
  const statusRef = useRef("idle");
  const speedRef = useRef(1);
  const layersRef = useRef({ marker: null, trails: [], raf: 0 });

  const setSt = (s) => {
    statusRef.current = s;
    setStatus(s);
  };

  const cleanup = () => {
    const map = mapRef.current;
    const l = layersRef.current;
    cancelAnimationFrame(l.raf);
    if (map) {
      if (l.marker) map.removeLayer(l.marker);
      l.trails.forEach((t) => map.removeLayer(t));
    }
    layersRef.current = { marker: null, trails: [], raf: 0 };
  };

  useEffect(() => cleanup, []); // eslint-disable-line react-hooks/exhaustive-deps

  const close = () => {
    cleanup();
    setSt("idle");
    setCaption(null);
    onActiveChange?.(false);
    const map = mapRef.current;
    if (map) map.setView([20, 10], 2, { animate: true });
  };

  const start = () => {
    const map = mapRef.current;
    if (!map) return;
    cleanup();
    onActiveChange?.(true);
    setSt("playing");

    const events = LIFE_JOURNEY;
    const l = layersRef.current;
    const first = events[0];
    const icon = L.divIcon({
      className: "atlas-pin-wrap",
      html: '<span class="cinema-ship"></span>',
      iconSize: [14, 14],
      iconAnchor: [7, 7],
    });
    l.marker = L.marker([first.lat, first.lng], { icon, zIndexOffset: 1000 }).addTo(map);
    let trail = L.polyline([[first.lat, first.lng]], {
      color: CH_COLORS[first.chapter],
      weight: 2,
      opacity: 0.85,
    }).addTo(map);
    l.trails.push(trail);
    map.setView([first.lat, first.lng], 5.5, { animate: false });
    setCaption(first);

    let i = 0;
    let phase = "hold";
    let phaseStart = performance.now();
    let lastNow = phaseStart;
    let legDur = 2200;
    let legPath = null;
    let legCum = null;
    let legLen = 1;
    let trailSea = true;

    // Smoothly adjust zoom once per stop (while holding) instead of every
    // frame during travel — per-frame zoom churn is what caused tile flicker.
    const preZoom = () => {
      if (i >= events.length - 1) return;
      const zNext = zoomForLeg(distKm(events[i], events[i + 1]));
      if (Math.abs(zNext - map.getZoom()) >= 0.4) {
        map.flyTo([events[i].lat, events[i].lng], zNext, {
          animate: true,
          duration: Math.min(0.6, (legDur / speedRef.current / 1000) * 0.85),
        });
      }
    };
    preZoom();

    const step = (now) => {
      const st = statusRef.current;
      if (st !== "playing" && st !== "paused") return;
      const dt = now - lastNow;
      lastNow = now;
      if (st === "paused") {
        phaseStart += dt;
        l.raf = requestAnimationFrame(step);
        return;
      }
      const elapsed = (now - phaseStart) * speedRef.current;
      if (phase === "hold") {
        if (elapsed >= legDur) {
          if (i >= events.length - 1) {
            setSt("done");
            try {
              map.fitBounds(events.map((e) => [e.lat, e.lng]), { padding: [40, 40] });
            } catch (_) {
              /* noop */
            }
            return;
          }
          const a = events[i];
          const b = events[i + 1];
          phase = "travel";
          phaseStart = now;
          const sea = isSeaLeg(a, b);
          legPath = sea
            ? seaRoute(a, b)
            : [
                [a.lat, a.lng],
                [b.lat, b.lng],
              ];
          legCum = [0];
          for (let k = 1; k < legPath.length; k++) {
            legCum.push(
              legCum[k - 1] +
                distKm(
                  { lat: legPath[k - 1][0], lng: legPath[k - 1][1] },
                  { lat: legPath[k][0], lng: legPath[k][1] }
                )
            );
          }
          legLen = legCum[legCum.length - 1] || 1;
          legDur = Math.min(Math.max(legLen * 1.1, 450), 3200);
          if (b.chapter !== a.chapter || sea !== trailSea) {
            trailSea = sea;
            trail = L.polyline([[a.lat, a.lng]], {
              color: CH_COLORS[b.chapter],
              weight: 2,
              opacity: 0.85,
              dashArray: sea ? null : "5 8",
            }).addTo(map);
            l.trails.push(trail);
          }
        }
      } else {
        const t = Math.min(elapsed / legDur, 1);
        const e = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
        const target = e * legLen;
        let k = 1;
        while (k < legCum.length - 1 && legCum[k] < target) k++;
        const segLen = legCum[k] - legCum[k - 1] || 1;
        const f = (target - legCum[k - 1]) / segLen;
        const lat = legPath[k - 1][0] + (legPath[k][0] - legPath[k - 1][0]) * f;
        const lng = legPath[k - 1][1] + (legPath[k][1] - legPath[k - 1][1]) * f;
        l.marker.setLatLng([lat, lng]);
        trail.addLatLng([lat, lng]);
        map.panTo([lat, lng], { animate: false });
        if (t >= 1) {
          i += 1;
          setCaption(events[i]);
          phase = "hold";
          phaseStart = now;
          legDur = events[i].major ? 2000 : 420;
          preZoom();
        }
      }
      l.raf = requestAnimationFrame(step);
    };
    l.raf = requestAnimationFrame(step);
  };

  const togglePause = () => {
    if (statusRef.current === "playing") setSt("paused");
    else if (statusRef.current === "paused") setSt("playing");
  };

  const toggleSpeed = () => {
    const next = speedRef.current === 4 ? 1 : speedRef.current * 2;
    speedRef.current = next;
    setSpeed(next);
  };

  return (
    <>
      {status === "idle" && (
        <button
          onClick={start}
          data-testid="cinema-play-btn"
          className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[1000] glass-chip inline-flex items-center gap-2.5 border border-[var(--c-accent)] bg-themed-90 px-5 py-3 font-mono-plex text-[11px] uppercase tracking-[0.2em] text-[var(--c-accent)] hover:bg-[var(--c-accent)] hover:text-[var(--c-bg)] transition-colors shadow-lg whitespace-nowrap"
        >
          <Play size={13} /> Play His Life · 1962 — Today
        </button>
      )}

      {(status === "playing" || status === "paused") && caption && (
        <div
          className="absolute bottom-4 left-4 right-16 sm:right-auto sm:max-w-sm z-[1000] border border-[var(--c-border)] bg-themed-90 backdrop-blur-xl p-4 sm:p-5"
          data-testid="cinema-caption"
        >
          <p
            className="font-mono-plex text-[9px] uppercase tracking-[0.22em]"
            style={{ color: CH_COLORS[caption.chapter] }}
          >
            {CH_LABEL[caption.chapter]}
          </p>
          <p className="font-serif-grand font-semibold italic text-2xl sm:text-3xl text-[var(--c-accent)] leading-none mt-1.5">
            {caption.year}
          </p>
          <p className="font-serif-display text-lg sm:text-xl text-[var(--c-text)] mt-1 leading-tight">
            {caption.title}
          </p>
          <p className="text-xs sm:text-sm text-[var(--c-text-muted)] mt-1 leading-snug">
            {caption.place}
            {caption.sub && caption.sub !== caption.place ? ` — ${caption.sub}` : ""}
          </p>
        </div>
      )}

      {(status === "playing" || status === "paused") && (
        <div className="absolute top-3 right-3 z-[1000] flex gap-1.5">
          <CtrlBtn
            onClick={togglePause}
            testid="cinema-pause-btn"
            label={status === "playing" ? "Pause" : "Resume"}
          >
            {status === "playing" ? <Pause size={13} /> : <Play size={13} />}
          </CtrlBtn>
          <CtrlBtn onClick={toggleSpeed} testid="cinema-speed-btn" label="Playback speed">
            <span className="font-mono-plex text-[10px]">{speed}×</span>
          </CtrlBtn>
          <CtrlBtn onClick={close} testid="cinema-stop-btn" label="Stop">
            <Square size={11} />
          </CtrlBtn>
        </div>
      )}

      {status === "done" && (
        <div className="absolute inset-0 z-[1000] flex items-center justify-center p-6 pointer-events-none">
          <div
            className="pointer-events-auto border border-[var(--c-accent)]/60 bg-themed-90 backdrop-blur-xl p-6 sm:p-8 text-center max-w-md"
            data-testid="cinema-end-card"
          >
            <p className="overline">1962 — Today</p>
            <p className="font-serif-grand font-semibold italic text-2xl sm:text-3xl text-[var(--c-text)] mt-3 leading-snug">
              Three chapters, one voyage — and counting.
            </p>
            <div className="mt-6 flex justify-center gap-2 font-mono-plex text-[10px] uppercase tracking-[0.2em]">
              <button
                onClick={start}
                className="border border-[var(--c-accent)] text-[var(--c-accent)] hover:bg-[var(--c-accent)] hover:text-[var(--c-bg)] px-4 py-2.5 transition-colors"
                data-testid="cinema-replay-btn"
              >
                Replay
              </button>
              <button
                onClick={close}
                className="border border-[var(--c-border)] text-[var(--c-text)] hover:border-[var(--c-accent)] px-4 py-2.5 transition-colors"
                data-testid="cinema-close-btn"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function CtrlBtn({ onClick, children, label, testid }) {
  return (
    <button
      onClick={onClick}
      title={label}
      aria-label={label}
      data-testid={testid}
      className="w-9 h-9 flex items-center justify-center border border-[var(--c-border)] bg-themed-90 backdrop-blur-xl text-[var(--c-text)] hover:text-[var(--c-accent)] hover:border-[var(--c-accent)] transition-colors"
    >
      {children}
    </button>
  );
}
